import { Box } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { SearchBarByText } from 'components/globals/searchBar/SearchBarByText';
import { FormDialog } from 'components/globals/dialog/FormDialog';
import { CustomDecideDialog } from 'components/globals/dialog/CustomDeleteDialog';

// Unidades Organizacionales
import { useOrganizationalUnitContext } from 'hooks/useUnidadOrganizacionalContext';
import { IOrganizationalUnit } from 'interfaces/organism/IOrganism';
import { OrganizationalUnitTable } from 'components/organizationalUnit/OrganizationalUnitTable';
import { OverviewOrganizationalUnitForm } from 'components/organizationalUnit/Overview';
import { IJurisdiction } from 'interfaces/jurisdictions/IJurisdictions';
import { useAppContext } from 'hooks/useAppContext';

export const ORGANIZATIONAL_UNITS_LABELS = {
	label: 'Unidad Organizacional',
	add: 'Agregar Unidad Organizacional',
	update: 'Editar Unidad Organizacional',
	delete: '¿Desea Eliminar la Unidad Organizacional?',
	list: 'Unidades Organizacionales',
	manage: 'Gestión de Unidades Organizacionales',
	searchLabel: 'Ingrese código o nombre de la Unidad Organizacional'
};

const OrganizationalUnitsOverview = memo(() => {
	const organizationalUnitContext = useOrganizationalUnitContext();
	const appContext = useAppContext();

	const [organizationalUnits, setOrganizationalUnits] = useState<IOrganizationalUnit[] | []>([]);
	const [jurisdictions, setJurisdictions] = useState<IJurisdiction[] | []>([]);

	useEffect(() => {
		appContext?.onHandleTryGetAllJurisdictions();
		organizationalUnitContext?.onHandleGetAll(organizationalUnitContext?.filters, {
			page: organizationalUnitContext?.meta?.currentPage,
			limit: organizationalUnitContext?.meta?.itemsPerPage
		});
	}, []);

	useEffect(() => {
		setOrganizationalUnits(organizationalUnitContext?.entities ? organizationalUnitContext?.entities : []);
	}, [organizationalUnitContext?.entities]);

	useEffect(() => {
		setJurisdictions(appContext?.jurisdictions);
	}, [appContext?.jurisdictions]);

	// START Handle Add entity Modal
	const [showManageEntityModal, toggleShowManageEntityModal] = useState<boolean>(false);

	// Unidades Organizacionales
	const [selectedOrganizationalUnit, setSelectedOrganizationalUnit] = useState<IOrganizationalUnit | undefined>(
		undefined
	);
	const handleToggleOrganizationalUnitModal = (
		show: boolean = false,
		entity: IOrganizationalUnit | undefined = undefined
	) => {
		setSelectedOrganizationalUnit(entity);
		toggleShowManageEntityModal(show);
	};

	const handleSubmitOrganizationalUnitModal = async (entity: IOrganizationalUnit | undefined) => {
		if (!entity) {
			return;
		}
		let result = await organizationalUnitContext?.onHandleCreateOrUpdate(entity);

		if (result) {
			handleToggleOrganizationalUnitModal();
			organizationalUnitContext?.onHandleGetAll(organizationalUnitContext?.filters, {
				page: organizationalUnitContext?.meta?.currentPage,
				limit: organizationalUnitContext?.meta?.itemsPerPage
			});
		}
	};
	// END Handle Add entity Modal

	// START Handle Delete entity Modal
	const [showDeleteEntityModal, toggleShowDeleteEntityModal] = useState<boolean>(false);

	const handleShowDeleteOrganizationalUnit = (entity: IOrganizationalUnit | undefined) => {
		if (!entity) {
			return;
		}
		setSelectedOrganizationalUnit(entity);
		toggleShowDeleteEntityModal(true);
	};

	const handleHideDeleteOrganizationalUnit = () => {
		setSelectedOrganizationalUnit(undefined);
		toggleShowDeleteEntityModal(false);
	};

	const handleSubmitDeleteOrganizationalUnit = async (entity: IOrganizationalUnit | undefined) => {
		if (!entity) {
			return;
		}
		let result = await organizationalUnitContext?.onHandleDelete(entity.id ? entity.id : null);

		if (result) {
			handleHideDeleteOrganizationalUnit();
			organizationalUnitContext?.onHandleGetAll(organizationalUnitContext?.filters, {
				page: organizationalUnitContext?.meta?.currentPage,
				limit: organizationalUnitContext?.meta?.itemsPerPage
			});
		}
	};
	// END Handle Delete entity Modal

	return (
		<>
			{showManageEntityModal && (
				<FormDialog
					show={showManageEntityModal}
					title={selectedOrganizationalUnit ? ORGANIZATIONAL_UNITS_LABELS.update : ORGANIZATIONAL_UNITS_LABELS.add}
					content={
						<OverviewOrganizationalUnitForm
							entity={selectedOrganizationalUnit}
							handleCancel={() => {
								handleToggleOrganizationalUnitModal();
							}}
							handleSubmit={(entity: IOrganizationalUnit) => {
								handleSubmitOrganizationalUnitModal(entity);
							}}
							jurisdictions={jurisdictions ? jurisdictions : []}
						/>
					}
					actions={<></>}
					handleClose={(value) => {
						handleToggleOrganizationalUnitModal();
					}}
				/>
			)}

			{showDeleteEntityModal && (
				<CustomDecideDialog
					message={ORGANIZATIONAL_UNITS_LABELS.delete}
					handleSubmit={() => {
						handleSubmitDeleteOrganizationalUnit(selectedOrganizationalUnit);
					}}
					handleDecline={() => {
						handleHideDeleteOrganizationalUnit();
					}}
				/>
			)}

			<Box sx={{ width: '100%', typography: 'body1' }}>
				<SearchBarByText
					label={ORGANIZATIONAL_UNITS_LABELS.searchLabel}
					defaultValue={organizationalUnitContext?.filters?.term}
					onHandleSearch={(ev: any) => {
						organizationalUnitContext?.onHandleSearByFilters({ term: ev });
					}}
					submitLabel={ORGANIZATIONAL_UNITS_LABELS.add}
					onHandleSubmit={(ev: any) => {
						handleToggleOrganizationalUnitModal(true);
					}}
					onHandleClear={(ev: any) => {
						organizationalUnitContext?.onHandleClearFilters();
					}}
				/>

				<Box sx={{ overflowX: 'scroll', marginTop: '5px' }}>
					<OrganizationalUnitTable
						elements={organizationalUnits}
						handleUpdate={(entity: any) => {
							handleToggleOrganizationalUnitModal(true, entity);
						}}
						handleDelete={(entity: any) => {
							handleShowDeleteOrganizationalUnit(entity);
						}}
					/>
				</Box>
			</Box>
		</>
	);
});

OrganizationalUnitsOverview.displayName = 'Parámetros';

export { OrganizationalUnitsOverview };
